import classNames from 'classnames'
import { AnimatePresence, motion, type HTMLMotionProps, type Transition } from 'framer-motion'
import React, {
  CSSProperties,
  DetailedHTMLProps,
  FC,
  Fragment,
  HTMLAttributes,
  PropsWithChildren,
  ReactNode,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
  createContext,
  useContext,
  type JSX,
} from 'react'
import { useTranslation } from 'react-i18next'

import { AlertIconWrapper } from '@ui/AlertIconWrapper/AlertIconWrapper'
import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Truncate } from '@ui/Truncate/Truncate'
import { AlignType, Body1, Body2, Caption1, Title1 } from '@ui/Typography'
import { IconButton } from '@ui/components/Button'
import { Checkbox } from '@ui/components/Checkbox'
import { Tooltip, TooltipProps } from '@ui/components/Tooltip/Tooltip'
import { TextColorType } from '@ui/design-system/colors/colors'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { IconFCType } from '@ui/icons/types'
import { Scrollbar } from '@ui/v2/Scrollbar/Scrollbar'
import { SkeletonRows } from '@ui/v2/Skeleton'
import { TableEmptyComponent } from '@ui/v2/Table/NoResults/TableNoResults'

import { SortOrder } from '@utils/ApiClient'

import { NameDto } from '@model/NameDto'

import { useHorizontalScroll } from '@hooks/useHorizontalScroll'

import { TMGrayLogo } from '@assets/logo/TMGray.logo'

import { EASE_OUT } from '@shared/animations/consts'
import { Box } from '@shared/components/Box'
import { CopyLink } from '@shared/components/CopyLink'
import { Loading } from '@shared/components/Loading/Loading'
import { TableActionsBox } from '@shared/components/TableActionsBox/TableActionsBox'

import styles from './Table.module.scss'

export type Sticky = 'left'
export type ColumnWidth = CSSProperties['width']
export interface ColumnSizeProps {
  width?: ColumnWidth
  minWidth?: ColumnWidth
  maxWidth?: ColumnWidth
}
export type TableSize = 'xs' | 'sm' | 'md' | 'lg'
export type TableProps = DetailedHTMLProps<
  TableHTMLAttributes<HTMLTableElement>,
  HTMLTableElement
> & {
  wrapperClassName?: string
  sticky?: Sticky
  isLoading?: boolean
  noResults?: ReactNode | boolean
  skeletonRows?: number // nr of skeleton rows to show during loading
  skeletonColumns?: number // nr of skeleton columns (requires if skeleton is set)
  topContent?: ReactNode
  hideScrollbarOnMobile?: boolean
  hideScrollbar?: boolean
  size?: TableSize
  isLayoutFixed?: boolean
}

const TableColumnStylesContext = createContext<CSSProperties[]>([])
const TableSizeContext = createContext<TableSize>('md')
const TableRowsAnimationContext = createContext({ enabled: false, animateEnter: false })

const animatedTableRowTransition: Transition = {
  opacity: { duration: 0.16, ease: 'easeOut' },
  y: { duration: 0.2, ease: EASE_OUT },
  layout: { type: 'spring', stiffness: 500, damping: 45, mass: 0.8 },
}

const getColumnWidthValue = (width: ColumnWidth | undefined) =>
  typeof width === 'number' ? `${width}px` : width

export const Table: FC<PropsWithChildren<TableProps>> = (props) => {
  const {
    children,
    noResults,
    isLoading = false,
    className,
    wrapperClassName,
    sticky,
    skeletonRows,
    skeletonColumns,
    topContent,
    size = 'md',
    hideScrollbarOnMobile,
    hideScrollbar,
    isLayoutFixed,
    ...restProps
  } = props

  const { t } = useTranslation()
  const {
    scrollContainerRef,
    scrollContainerInnerRef,
    hasScrolledToEnd,
    hasScrolledToStart,
    isScrollable,
  } = useHorizontalScroll<HTMLTableElement>({
    shouldRestorePosition: true,
    dependencies: [isLoading],
  })

  // use skeleton loading if skeletonRows is specified
  const useSkeleton = skeletonRows && skeletonColumns

  const childrenArray = React.Children.toArray(children)
  const headerElement = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === TableHeader
  )
  const headerColumnStyles = getHeaderColumnStyles(headerElement)

  return (
    <TableColumnStylesContext.Provider value={headerColumnStyles}>
      <TableSizeContext.Provider value={size}>
        <div
          className={classNames(styles.tableWrapper, wrapperClassName, {
            [styles.sizeXS]: size === 'xs',
            [styles.sizeSM]: size === 'sm',
            [styles.sizeMD]: size === 'md',
            [styles.sizeLG]: size === 'lg',
            [styles.containerScrollable]: isScrollable,
            [styles.scrollContainerFadeStart]:
              sticky !== 'left' && isScrollable && !hasScrolledToStart,
            [styles.scrollContainerFadeEnd]: isScrollable && !hasScrolledToEnd,
            [styles.stickyLeft]: sticky === 'left' && !hasScrolledToStart,
            [styles.noResults]: !!noResults && !isLoading,
          })}
        >
          {topContent && (
            <div className={styles.stickyTop}>
              <div className={styles.tableTopContent}>{topContent}</div>
            </div>
          )}
          <div className={styles.scrollContainer} ref={scrollContainerRef}>
            <table
              ref={scrollContainerInnerRef}
              className={classNames(
                styles.table,
                { [styles.fixedLayout]: isLayoutFixed },
                className
              )}
              {...restProps}
            >
              {useSkeleton && isLoading ? (
                // skeleton loading mode: show header + skeleton rows
                <>
                  {headerElement}
                  <TableBody>
                    <SkeletonRows rows={skeletonRows} columns={skeletonColumns} />
                  </TableBody>
                </>
              ) : (
                // default loading mode: use Loading component with overlay
                <Loading fullWidth isLoading={isLoading} showLoadingIcon>
                  {noResults ? (
                    noResults === true ? (
                      <TableNoResults title={t('No results')} />
                    ) : (
                      noResults
                    )
                  ) : (
                    children
                  )}
                </Loading>
              )}
            </table>
          </div>
          {!hideScrollbar && (
            <Scrollbar
              orientation='horizontal'
              isScrollableOverride={isScrollable}
              parentRef={scrollContainerRef}
              hideOnMobile={hideScrollbarOnMobile}
            />
          )}
        </div>
      </TableSizeContext.Provider>
    </TableColumnStylesContext.Provider>
  )
}

type TableHeaderProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableSectionElement>,
  HTMLTableSectionElement
> & {
  selectedIsActive?: boolean
}

export const TableHeader: FC<PropsWithChildren<TableHeaderProps>> = (props) => {
  const { children, className, selectedIsActive, ...restProps } = props

  return (
    <thead
      {...restProps}
      className={classNames(styles.tableHeader, className, {
        [styles.selectedIsActive]: selectedIsActive,
      })}
    >
      {children}
    </thead>
  )
}

type TableBodyProps = DetailedHTMLProps<
  HTMLAttributes<HTMLTableSectionElement>,
  HTMLTableSectionElement
> & {
  animateNewRows?: boolean
  pageKey?: React.Key
}

const hasColumnSize = ({ width, minWidth, maxWidth }: Partial<ColumnSizeProps>) =>
  width !== undefined || minWidth !== undefined || maxWidth !== undefined

const getColumnSizeStyle = ({
  width,
  minWidth,
  maxWidth,
}: ColumnSizeProps): CSSProperties | undefined => {
  if (!hasColumnSize({ width, minWidth, maxWidth })) return undefined

  const columnWidth = getColumnWidthValue(width)
  const columnMinWidth = getColumnWidthValue(minWidth)
  const columnMaxWidth = getColumnWidthValue(maxWidth)
  const effectiveWidth = columnWidth ?? columnMaxWidth
  const effectiveMinWidth = columnMinWidth ?? (columnMaxWidth !== undefined ? 0 : undefined)

  return {
    ...(effectiveWidth !== undefined && { width: effectiveWidth }),
    ...(effectiveMinWidth !== undefined && { minWidth: effectiveMinWidth }),
    ...(columnMaxWidth !== undefined && { maxWidth: columnMaxWidth }),
    overflow: 'hidden',
  }
}

export const TableBody: FC<PropsWithChildren<TableBodyProps>> = (props) => {
  const { animateNewRows = false, pageKey, children, className, ...restProps } = props
  const hasMounted = React.useRef(false)
  const previousPageKey = React.useRef(pageKey)
  const pageChanged = previousPageKey.current !== pageKey

  React.useLayoutEffect(() => {
    hasMounted.current = true
    previousPageKey.current = pageKey
  }, [pageKey])

  const animationContext = {
    enabled: animateNewRows,
    animateEnter: animateNewRows && hasMounted.current && !pageChanged,
  }

  return (
    <TableRowsAnimationContext.Provider value={animationContext}>
      <tbody className={classNames(styles.tableBody, className)} {...restProps}>
        {children}
      </tbody>
    </TableRowsAnimationContext.Provider>
  )
}

interface ExpandableRowProps<RowType, SubType = RowType> {
  id?: string
  row: RowType & { id: string; subRows?: SubType[] }
  isExpanded: boolean
  onToggle: () => void
  renderParent: (row: RowType) => { value: ReactNode; align?: AlignType; width?: number }[]
  renderSubRow: (row: SubType) => { value: ReactNode; align?: AlignType; width?: number }[]
  onSubRowClick?: (row: SubType) => void
  className?: string
  isSensitiveData?: boolean
  hasTruncate?: boolean
  chevronPosition?: 'left' | 'right'
}

export const ExpandableRow = <RowType, SubType extends { id: string } = RowType & { id: string }>({
  id,
  row,
  isExpanded,
  onToggle,
  renderParent,
  renderSubRow,
  onSubRowClick,
  className,
  isSensitiveData,
  chevronPosition = 'right',
  hasTruncate = false,
}: ExpandableRowProps<RowType, SubType>): JSX.Element => {
  const handleToggle = () => {
    if (row.subRows?.length) {
      onToggle()
    }
  }

  const parentCells = renderParent(row)

  return (
    <Fragment key={row.id}>
      <TableRow
        id={id}
        className={classNames(styles.expandableParentRow, {
          [styles.expandedParentRow]: isExpanded,
        })}
      >
        <TableCell
          onClick={handleToggle}
          className={classNames(styles.expandToggleCell, className)}
          hasTruncate={false}
        >
          <div className={styles.expandToggleCellInnerWrapper}>
            {chevronPosition === 'left' && row.subRows?.length ? (
              <motion.span
                initial={false}
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <Box flex>
                  <CaretIcon.Down size={20} />
                </Box>
              </motion.span>
            ) : null}
            {shouldRenderAsSensitiveData(parentCells[0].value, !!isSensitiveData)}
            {chevronPosition === 'right' && row.subRows?.length ? (
              <motion.span
                initial={false}
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <Box flex>
                  <CaretIcon.Down size={20} />
                </Box>
              </motion.span>
            ) : null}
          </div>
        </TableCell>

        {parentCells.slice(1).map((cell, i) => (
          <TableCell
            hasTruncate={hasTruncate}
            key={i}
            align={cell.align}
            width={cell.width}
            className={className}
          >
            {shouldRenderAsSensitiveData(cell.value, !!isSensitiveData)}
          </TableCell>
        ))}
      </TableRow>

      <AnimatePresence initial={false}>
        {isExpanded &&
          row.subRows?.map((sub) => {
            const subCells = renderSubRow(sub)
            return (
              <motion.tr
                key={sub.id}
                className={classNames(styles.tableRow, styles.expandedRow, className, {
                  [styles.clickableSubRow]: !!onSubRowClick,
                })}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                layout='position'
                onClick={() => onSubRowClick?.(sub)}
              >
                {subCells.map((cell, i) => (
                  <TableCell
                    hasTruncate={hasTruncate}
                    align={cell.align}
                    width={cell.width}
                    key={i}
                  >
                    {shouldRenderAsSensitiveData(cell.value, !!isSensitiveData)}
                  </TableCell>
                ))}
              </motion.tr>
            )
          })}
      </AnimatePresence>
    </Fragment>
  )
}

type TableRowProps = DetailedHTMLProps<HTMLAttributes<HTMLTableRowElement>, HTMLTableRowElement> & {
  selected?: boolean
}

const getColumnConstraintWidth = (props: Partial<ColumnSizeProps>) =>
  getColumnWidthValue(props.width ?? props.maxWidth ?? props.minWidth)

const getDynamicColumnWidth = (dynamicWidthColCount: number, constrainedWidths: ColumnWidth[]) => {
  if (!dynamicWidthColCount) return '100%'
  if (!constrainedWidths.length) return `${100 / dynamicWidthColCount}%`

  return `calc((100% - ${constrainedWidths.join(' - ')}) / ${dynamicWidthColCount})`
}

const getColumnSizeProps = (child: React.ReactElement): Partial<ColumnSizeProps> => {
  const props = child.props as Partial<ColumnSizeProps>

  return {
    width: props.width,
    minWidth: props.minWidth,
    maxWidth: props.maxWidth,
  }
}

const getColumnStyles = (items: React.ReactNode[]) => {
  const dynamicWidthColCount = items.filter((i) => {
    if (!React.isValidElement(i)) return true
    return !hasColumnSize(getColumnSizeProps(i))
  }).length

  const constrainedWidths = items.flatMap((i) => {
    if (!React.isValidElement(i)) return []
    const constrainedWidth = getColumnConstraintWidth(getColumnSizeProps(i))
    return constrainedWidth ? [constrainedWidth] : []
  })

  const colWidth = getDynamicColumnWidth(dynamicWidthColCount, constrainedWidths)

  return items.map((i) => {
    if (!React.isValidElement(i)) return {}
    const columnSizeProps = getColumnSizeProps(i)

    return (
      getColumnSizeStyle(hasColumnSize(columnSizeProps) ? columnSizeProps : { width: colWidth }) ??
      {}
    )
  })
}

const getHeaderColumnStyles = (headerElement: React.ReactNode) => {
  if (!React.isValidElement(headerElement)) return []

  const headerProps = headerElement.props as PropsWithChildren
  const row = React.Children.toArray(headerProps.children).find(React.isValidElement)

  if (!React.isValidElement(row)) return []
  const rowProps = row.props as PropsWithChildren
  return getColumnStyles(React.Children.toArray(rowProps.children))
}

export const TableRow: FC<PropsWithChildren<TableRowProps>> = ({
  children,
  className,
  selected,
  ...restProps
}) => {
  const rowAnimation = React.useContext(TableRowsAnimationContext)
  const inheritedColumnStyles = React.useContext(TableColumnStylesContext)
  const items = React.Children.toArray(children)
  const columnStyles = inheritedColumnStyles.length ? inheritedColumnStyles : getColumnStyles(items)

  const rowClassName = classNames(styles.tableRow, className, {
    [styles.selected]: selected,
    [styles.clickableRow]: !!restProps.onClick,
  })

  const wrappedItems = items.map((child, index) => {
    if (!React.isValidElement<{ style?: CSSProperties }>(child)) return child
    return React.cloneElement(child, {
      style: { ...columnStyles[index], ...child.props.style },
    })
  })

  if (rowAnimation.enabled) {
    return (
      <motion.tr
        initial={rowAnimation.animateEnter ? { opacity: 0, y: -6 } : false}
        animate={{ opacity: 1, y: 0 }}
        layout
        transition={animatedTableRowTransition}
        className={rowClassName}
        {...(restProps as HTMLMotionProps<'tr'>)}
      >
        {wrappedItems}
      </motion.tr>
    )
  }

  return (
    <tr className={rowClassName} {...restProps}>
      {wrappedItems}
    </tr>
  )
}

type TableHeadProps = DetailedHTMLProps<
  ThHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> &
  ColumnSizeProps & {
    onSelectAll?(): void
    allSelected?: boolean
    someSelected?: boolean
    selectDisabled?: boolean
  }

export const TableHead: FC<PropsWithChildren<TableHeadProps>> = (props) => {
  const {
    children,
    className,
    style,
    width,
    minWidth,
    maxWidth,
    onSelectAll,
    allSelected,
    someSelected,
    selectDisabled,
    ...restProps
  } = props

  return (
    <th
      className={classNames(styles.tableHead, className)}
      style={{
        ...getColumnSizeStyle({ width, minWidth, maxWidth }),
        ...style,
      }}
      {...restProps}
    >
      {onSelectAll ? (
        <Box flex gap='x8' align='center'>
          <div className={styles.checkboxWrapper}>
            <Checkbox
              size='sm'
              disabled={selectDisabled}
              checked={!!allSelected}
              intermediate={!allSelected && !!someSelected}
              onChange={onSelectAll}
            />
          </div>
          <Caption1 medium color='text-med-em'>
            {children}
          </Caption1>
        </Box>
      ) : (
        <Caption1 medium color='text-med-em'>
          {children}
        </Caption1>
      )}
    </th>
  )
}

interface Props extends TableHeadProps {
  id?: string
  className?: string
  style?: CSSProperties
  align?: SortHeaderAlignType
  sort?: string | { sort: string; sortOrder: SortOrder }[] | null
  sortOrder?: SortOrder
  startWithOrder?: SortOrder

  setSort?(id: string, order: SortOrder): void | undefined
}

export type SortOptions<T> = (NameDto<T | null | undefined> &
  ColumnSizeProps & {
    isNumeric?: boolean
    visibleOnMobile?: boolean
  })[]
export type SortHeaderAlignType = 'center' | 'left' | 'right' | 'justify' | 'char'

export const TableHeadSort: FC<PropsWithChildren<Props>> = (props) => {
  const { id, sort, sortOrder, startWithOrder, style, className, setSort, ...rest } = props

  const currentSort = Array.isArray(sort)
    ? sort.find((s) => s.sort === id)?.sortOrder
    : sort === id
    ? sortOrder
    : null

  const selected = !!currentSort
  const isASC = currentSort === 'ASC'
  const isDESC = currentSort === 'DESC'

  let nextSort: SortOrder = isASC ? 'DESC' : 'ASC'

  if (id !== sort) {
    nextSort = startWithOrder || 'ASC'
  }

  return (
    <TableHead
      style={style}
      className={classNames(className)}
      onClick={(event) => {
        const target = event.target as HTMLElement

        const isTarget = target.closest('.tableHeadSort')
        if (isTarget && id) setSort?.(id, nextSort)
      }}
      {...rest}
    >
      <span
        className={classNames(styles.tableHeadSort, 'tableHeadSort', {
          [styles.alignLeft]: props.align === 'left',
          [styles.alignCenter]: props.align === 'center',
          [styles.alignRight]: props.align === 'right',
        })}
      >
        {props.children}
        {selected && isASC && <CaretIcon.Up size={12} />}
        {selected && isDESC && <CaretIcon.Down size={12} />}
      </span>
    </TableHead>
  )
}

type TableCellProps = DetailedHTMLProps<
  TdHTMLAttributes<HTMLTableCellElement>,
  HTMLTableCellElement
> &
  ColumnSizeProps & {
    checkboxTooltipText?: string
    onSelect?(): void
    disabled?: boolean
    selected?: boolean
    alert?: boolean
    contentFullWidth?: boolean
    hasTruncate?: boolean
    color?: TextColorType
    onExpand?: () => void
    expanded?: boolean
    sensitive?: boolean
  }

export const TableCell: FC<PropsWithChildren<TableCellProps>> = (props) => {
  const {
    className,
    onSelect,
    disabled,
    selected,
    checkboxTooltipText,
    alert,
    contentFullWidth,
    expanded,
    children,
    onExpand,
    hasTruncate = true,
    sensitive = false,
    color: _color = 'text-high-em',
    style,
    width,
    minWidth,
    maxWidth,
    ...rest
  } = props

  void _color
  const size = useContext(TableSizeContext)
  const truncatedChildren = hasTruncate ? (
    <Truncate>
      {size === 'xs' ? (
        <Caption1 color='text-med-em'>{children}</Caption1>
      ) : (
        <Body2 color='text-med-em'>{children}</Body2>
      )}
    </Truncate>
  ) : (
    children
  )

  const data = sensitive ? <SensitiveData>{truncatedChildren}</SensitiveData> : truncatedChildren

  return (
    <td
      {...rest}
      style={{
        ...getColumnSizeStyle({ width, minWidth, maxWidth }),
        ...style,
      }}
      className={classNames(styles.tableCell, className, {
        [styles.clickable]: !!rest.onClick,
      })}
    >
      <AlertIconWrapper
        className={classNames(styles.cellAlert, {
          [styles.alignLeft]: rest.align === 'left',
          [styles.alignCenter]: rest.align === 'center',
          [styles.alignRight]: rest.align === 'right',
        })}
        asElement
        gapped
        isActive={alert}
        fullWidth={contentFullWidth}
      >
        <div
          className={classNames(styles.cellInnerWrapper, {
            [styles.fullWidth]: contentFullWidth,
          })}
        >
          {onSelect && (
            <div className={styles.checkboxWrapper}>
              <Tooltip direction='topRight' content={checkboxTooltipText}>
                <Checkbox size='sm' disabled={disabled} checked={!!selected} onChange={onSelect} />
              </Tooltip>
            </div>
          )}

          {onExpand ? (
            <Box flex gap='x8' align='center' onClick={onExpand}>
              <motion.span
                initial={false}
                animate={{ rotate: expanded ? 180 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <Box flex>
                  <ChevronIcon.Down />
                </Box>
              </motion.span>
              {data}
            </Box>
          ) : (
            data
          )}
        </div>
      </AlertIconWrapper>
    </td>
  )
}

export const SensitiveTableCell: FC<PropsWithChildren<TableCellProps>> = ({
  children,
  ...cellProps
}) => {
  return (
    <TableCell {...cellProps}>
      <SensitiveData>{children}</SensitiveData>
    </TableCell>
  )
}

interface ExpandedRowProps extends PropsWithChildren {
  colSpan: number
  expanded?: boolean
}

const hasNonEmptyValue = (v: ExpandedItemProps['value']) =>
  !(v === undefined || v === null || (typeof v === 'string' && v.trim() === ''))

export const ExpandedRow: FC<ExpandedRowProps> = ({ colSpan, children, expanded }) => {
  const variants = {
    open: { height: 'auto', opacity: 1, transition: { duration: 0.25, ease: 'easeInOut' } },
    collapsed: { height: 0, opacity: 0, transition: { duration: 0.2, ease: 'easeInOut' } },
  } as const

  const raw = React.Children.toArray(children)

  const filtered = raw.filter((child) => {
    if (!React.isValidElement(child)) {
      if (typeof child === 'string') return child.trim() !== ''
      return child != null
    }

    const props = child.props as Partial<ExpandedItemProps>
    const valueOk = hasNonEmptyValue(props.value as ExpandedItemProps['value'])
    const conditionOk = props.condition !== false
    return valueOk && conditionOk
  })

  if (!filtered.length) return null

  const content = filtered.flatMap((child, i) =>
    i < filtered.length - 1
      ? [child, <div key={`divider-${i}`} className={styles.divider} />]
      : [child]
  )

  return (
    <TableRow className={classNames(styles.expandedRowCell, { [styles.expanded]: expanded })}>
      <TableCell hasTruncate={false} colSpan={colSpan}>
        <motion.div
          variants={variants}
          initial='collapsed'
          animate={expanded ? 'open' : 'collapsed'}
          style={{ overflow: 'hidden', width: '100%' }}
          layout='size'
        >
          <Box fullWidth wrap px='x16' py='x8' flex direction='row' align='flex-start' gap='x12'>
            {content}
          </Box>
        </motion.div>
      </TableCell>
    </TableRow>
  )
}

interface ExpandedItemProps {
  label: string
  value?: string | number
  sensitive?: boolean
  canCopy?: boolean
  preventClickCopy?: boolean
  condition?: boolean
  icon?: IconFCType
  onIconClick?: () => void
  info?: TooltipProps
}

const validateExpandedItem = (item: ExpandedItemProps) =>
  !hasNonEmptyValue(item.value) || item.condition === false

export const ExpandedItem: FC<ExpandedItemProps> = (props) => {
  const { label, value, sensitive, canCopy, info, icon: Icon, onIconClick } = props
  if (validateExpandedItem(props)) return null

  return (
    <Box flex gap='x8' className={styles.expandedItem} align='center'>
      <Body2 wrap='nowrap' color='text-low-em'>
        {label}:
      </Body2>
      <Truncate>
        <Body2 color='text-high-em'>
          {sensitive ? <SensitiveData>{value}</SensitiveData> : value}
        </Body2>
      </Truncate>
      {canCopy && (
        <CopyLink value={value} disabled={sensitive}>
          <CopyIcon color='text-med-em' size={18} />
        </CopyLink>
      )}
      {Icon && <IconButton icon={Icon} onClick={onIconClick} />}
      {info && (
        <Tooltip {...info}>
          <InfoIcon color='text-med-em' size={18} />
        </Tooltip>
      )}
    </Box>
  )
}

interface TableNoResultsProps extends PropsWithChildren {
  title?: string
  content?: ReactNode
  className?: string
  isAnyFilterActive?: boolean
  renderTitle?(): ReactNode
}

export const TableNoResults: FC<TableNoResultsProps> = (props) => {
  const { content, children, className, isAnyFilterActive } = props
  const { t } = useTranslation()
  const { title = t('No results') } = props

  return (
    <div className={styles.tableNoResults}>
      <TableEmptyComponent />
      <div className={classNames(styles.infoWrapper, className)}>
        <TMGrayLogo />
        {props.renderTitle ? props.renderTitle() : <Title1 medium>{title}</Title1>}
        {isAnyFilterActive && <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1>}
        <div>{content}</div>
        {children}
      </div>
    </div>
  )
}

const shouldRenderAsSensitiveData = (children: React.ReactNode, isSensitive: boolean) => {
  return isSensitive ? <SensitiveData>{children}</SensitiveData> : children
}
interface TableBulkActionsProps {
  selectedCount: number
  isCheckboxActive?: boolean
  isCheckboxIndeterminate?: boolean
  isCheckboxDisabled?: boolean
  onCheckboxClick?: () => void
  children: ReactNode
}

export const TableBulkActions: FC<TableBulkActionsProps> = ({
  selectedCount,
  children,
  ...props
}) => {
  const { t } = useTranslation()

  return (
    <TableActionsBox {...props}>
      <Caption1 color='text-white'>
        {t('IB.RebatesPage.ClientsPage.Selected', {
          count: selectedCount,
        })}
      </Caption1>

      <Box flex align='center' gap='x8' wrap ml='auto'>
        {children}
      </Box>
    </TableActionsBox>
  )
}
